import React, { useState, useEffect } from 'react'
import { Button, Input, InputNumber, Select, Space, Popconfirm, Flex, Tag, Divider, Row, Col, message, notification } from 'antd'

import { BILLING_CLONE, BILLING_SAVE, BILLING_CHARGE_SENT, PAYMENT_VOID, requestToServer } from '../lib/helper'
import { paymetMethod } from '../config'
import { BILLING_STATES, PLANS, PERIODS, dueTag, fmtDay, fmtStamp, fmtMoney } from '../lib/billing'
import PaymentModal from './PaymentModal'

// Task007: ficha de cobro del clon. Carga de /billing/clone, que lee solo la base de Pulpo: funciona aunque la base
// del clon esté caída (a diferencia de /clone/info, que la abre).
// Task009: onChanged (opcional) avisa a Clone.js que una acción escribió en logs, para recargar el log de la ficha
export default function BillingCard({ platformName, onChanged }) {

  const [data, setData] = useState(null)      // respuesta de /billing/clone
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [payOpen, setPayOpen] = useState(false)

  // todas las acciones devuelven el detalle completo del clon: se aplica igual que la carga inicial
  const apply = (r) => {
    setData(r)
    const c = r.clone
    setForm({
      billingState: c.billingState,
      plan: c.plan || undefined,
      billingPeriod: c.billingPeriod || undefined,
      amount: c.amount,
      currency: c.currency || 'USD',
      nextDueDate: c.nextDueDate || ''
    })
  }
  // Task009: las acciones (no la carga inicial) aplican la respuesta y avisan que hay una fila nueva en logs
  const applyChanged = (r) => { apply(r); if ( onChanged ) { onChanged() } }
  const load = () => requestToServer({ platformName }, BILLING_CLONE).then(apply).catch(()=>{})

  useEffect(() => { load() }, [platformName])

  const set = (key) => (value) => setForm( f => ({ ...f, [key]: value }) )
  // al elegir plan y período, si no hay monto se sugiere el de la lista; el monto sigue siendo editable
  const setPlanPeriod = (key) => (value) => setForm( f => {
    const next = { ...f, [key]: value }
    const list = data.prices[next.plan]
    if ( ( f.amount === null || f.amount === undefined ) && list && list[next.billingPeriod] != null ) {
      next.amount = list[next.billingPeriod]
    }
    return next
  })

  const save = () => {
    setSaving(true)
    requestToServer({
      platformName, ...form,
      plan: form.plan || null,
      billingPeriod: form.billingPeriod || null,
      amount: form.amount === undefined ? null : form.amount,
      nextDueDate: form.nextDueDate || null
    }, BILLING_SAVE)
      .then( r => { message.info('Cobro actualizado'); applyChanged(r) } )
      .catch( () => {} )
      .finally( () => setSaving(false) )
  }

  const chargeSent = (sent) => requestToServer({ platformName, sent }, BILLING_CHARGE_SENT).then(applyChanged).catch(()=>{})

  if ( !data ) { return null }
  const c = data.clone, due = c.due

  return (
    // sin relleno lateral: Clone.js ya lo da en el bloque donde va la ficha. El divisor de arriba la separa del
    // formulario del clon; el de abajo no hace falta porque "Enviar factura" trae el suyo
    <div style={{ paddingTop: '15px' }}>
      <Divider style={{ marginTop: 0 }} />

      <Flex justify='space-between' align='center' wrap='wrap' gap='small'>
        <strong>Cobro</strong>
        <Space>
          { due.autoCharge && <Tag style={{ marginInlineEnd: 0 }}>PayPal automático</Tag> }
          { dueTag(due) }
        </Space>
      </Flex>

      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        <Col xs={24} sm={8}>
          <Select style={{ width: '100%' }} value={form.billingState} onChange={set('billingState')}
            options={Object.keys(BILLING_STATES).map( k => ({ value: k, label: BILLING_STATES[k] }) )} />
        </Col>
        <Col xs={12} sm={8}>
          <Select allowClear style={{ width: '100%' }} placeholder='Plan' value={form.plan} onChange={setPlanPeriod('plan')}
            options={Object.keys(PLANS).map( k => ({ value: k, label: PLANS[k] }) )} />
        </Col>
        <Col xs={12} sm={8}>
          <Select allowClear style={{ width: '100%' }} placeholder='Período' value={form.billingPeriod} onChange={setPlanPeriod('billingPeriod')}
            options={Object.keys(PERIODS).map( k => ({ value: k, label: PERIODS[k] }) )} />
        </Col>
        <Col xs={12} sm={8}>
          <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder='Monto por período'
            value={form.amount} onChange={set('amount')} />
        </Col>
        <Col xs={12} sm={8}>
          <Input maxLength={3} placeholder='Moneda' value={form.currency}
            onChange={e => set('currency')(e.target.value.toUpperCase())} />
        </Col>
        <Col xs={24} sm={8}>
          <Input type='date' value={form.nextDueDate} onChange={e => set('nextDueDate')(e.target.value)} />
        </Col>
      </Row>
      <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>Próximo vencimiento: el campo de fecha. El método de pago y el id de suscripción están más abajo, en el formulario del clon.</div>

      <Space wrap style={{ marginTop: 10 }}>
        <Button loading={saving} onClick={save}>Guardar cobro</Button>
        <Button type='primary' onClick={() => setPayOpen(true)}>Registrar pago</Button>
        { c.billingState === 'active' && !due.autoCharge && ( c.chargeSentAt
          ? <Button onClick={() => chargeSent(false)}>Quitar "cobro enviado"</Button>
          : <Button onClick={() => chargeSent(true)}>Cobro enviado</Button> ) }
      </Space>
      { c.chargeSentAt && <div style={{ marginTop: 6 }}>Cobro enviado el {fmtStamp(c.chargeSentAt)}</div> }
      { c.billingState === 'suspended' && c.suspendedAt && <div style={{ marginTop: 6 }}>Suspendido el {fmtStamp(c.suspendedAt)}</div> }

      { data.payments.length > 0 && <>
        <Divider orientation='left' plain style={{ marginBottom: 6 }}>Últimos pagos</Divider>
        { data.payments.map( p => (
          <Flex key={p.id} justify='space-between' align='center' wrap='wrap' gap='small'
            style={{ opacity: p.voidedAt ? 0.45 : 1 }}>
            <span style={{ textDecoration: p.voidedAt ? 'line-through' : 'none', overflowWrap: 'anywhere' }}>
              {fmtDay(p.paidAt)} · {fmtMoney(p.amount, p.currency)} · {paymetMethod[p.method] || p.method} · cubre hasta {fmtDay(p.coversUntil)}
              { p.reference && <span> · {p.reference}</span> }
              { p.createdByUser && <span style={{ color: '#888' }}> · {p.createdByUser.email}</span> }
            </span>
            { !p.voidedAt &&
              <Popconfirm title='¿Anular este pago?' okText='Anular' cancelText='No'
                onConfirm={ () => requestToServer({ id: p.id }, PAYMENT_VOID).then( r => {
                  applyChanged(r)
                  notification.info({
                    message: 'Pago anulado',
                    description: r.restored ? 'Vencimiento restaurado a ' + fmtDay(r.restoredTo) : 'El vencimiento no se tocó: revísalo a mano.'
                  })
                }).catch(()=>{}) }>
                <Button size='small' type='link' danger>Anular</Button>
              </Popconfirm> }
          </Flex>
        ) ) }
      </> }

      <PaymentModal
        open={payOpen}
        clone={c}
        prices={data.prices}
        onClose={() => setPayOpen(false)}
        onDone={ r => {
          setPayOpen(false)
          applyChanged(r)
          // el pago reactiva el cobro pero no levanta el clon (nada arranca solo)
          if ( r.wasSuspended ) {
            notification.warning({
              message: 'El clon estaba suspendido',
              description: 'El pago reactivó el cobro, pero la plataforma sigue detenida: levántala con el botón Levantar.'
            })
          }
        }}
      />
    </div>
  )
}
